import { getGatewayBaseUrl } from "../config";
import {
  calculateFinalPayableRounding,
  finalPayableClientSnapshot,
  type FinalPayableRoundingResult,
} from "../commerce/final-payable-rounding";
import {
  PosCashSaleGatewayClient,
  type CashSaleGatewayLine,
  type CashSaleSyncResult,
  type PromotionClaimEvidence,
} from "../gateway/cash-sale-client";
import type { CommerceCalculationRuntimeItem } from "../gateway/local-projection-client";
import type { EmployeeRuntimeActor } from "../gateway/identity-client";
import {
  normalizePromotionClaimEvidence,
  type PromotionClaimEvaluation,
} from "../promotion/promotion-claim-session";
import {
  localCartSaleFingerprint,
  type LocalCartDraft,
  type LocalSaleMember,
  type LocalSaleQuote,
} from "./cart-state";
import {
  acknowledgeLocalCustomerBenefitUsage,
  writeLocalCustomerBenefitUsage,
} from "./customer-benefit-runtime";
import {
  clearLocalLoyaltyRedemption,
  readLocalLoyaltyRedemption,
  type LocalLoyaltyRedemption,
} from "./loyalty-redemption-state";
import { posRuntimeDb, type OutboxRecord } from "./pos-db";
import type { CashShiftSnapshot } from "../gateway/identity-client";
import type { TrustedTerminalIdentity } from "./terminal-state";

export type LocalCashSaleSnapshot = {
  schema: "LOCAL_CASH_SALE_V1";
  localTransactionId: string;
  rootRequestKey: string;
  organizationId: string;
  branchId: string;
  nodeId: string;
  commerceChannelId: string;
  performingEmployeeId: string;
  responsibleShiftEmployeeId: string;
  shiftId: string;
  catalogRevision: number;
  lines: LocalCartDraft["lines"];
  selectedMember: LocalSaleMember | null;
  claimedPromotionVersionIds: string[];
  quote: LocalSaleQuote | null;
  loyaltyRedemption: LocalLoyaltyRedemption | null;
  finalPayable?: FinalPayableRoundingResult;
  localTotal: number;
  tenderedAmount: number;
  changeAmount: number;
  currencyCode: string;
  localCommittedAt: string;
  canonical: CashSaleSyncResult | null;
};

export type LocalCashPaymentSnapshot = {
  schema: "LOCAL_CASH_PAYMENT_V1";
  localTransactionId: string;
  rootRequestKey: string;
  paymentMethodCode: "CASH";
  amount: number;
  preRoundingTotal?: number;
  roundingAmount?: number;
  calculationSettingsVersionId?: string;
  tenderedAmount: number;
  changeAmount: number;
  currencyCode: string;
  performingEmployeeId: string;
  shiftId: string;
  localCommittedAt: string;
};

export type CommitLocalCashSaleInput = {
  identity: TrustedTerminalIdentity;
  actor: EmployeeRuntimeActor;
  shift: CashShiftSnapshot;
  cart: LocalCartDraft;
  catalogRevision: number;
  commerceCalculation: CommerceCalculationRuntimeItem;
  tenderedAmount: number;
  onlineAtCommit: boolean;
  promotionClaimEvidence?: PromotionClaimEvidence[];
  promotionClaimEvaluation?: PromotionClaimEvaluation | null;
};

function randomId() {
  return typeof crypto !== "undefined" && "randomUUID" in crypto
    ? crypto.randomUUID()
    : `${Date.now()}-${Math.random().toString(16).slice(2)}`;
}

function saleScope(identity: TrustedTerminalIdentity) {
  return `org:${identity.organization_id}:branch:${identity.branch_id}:node:${identity.node_id}`;
}

function saleLocalId(localTransactionId: string) {
  return `sale:${localTransactionId}`;
}

function paymentLocalId(localTransactionId: string) {
  return `payment:${localTransactionId}:1`;
}

function rawCartTotal(cart: LocalCartDraft) {
  return cart.lines.reduce((sum, line) => sum + line.unitPrice * line.quantity, 0);
}

function customerClaimEvidence(value: PromotionClaimEvidence[]) {
  return value.filter((item): item is { claim_instance_id: string; customer_id: string } => "claim_instance_id" in item);
}

function bearerClaimEvidence(value: PromotionClaimEvidence[]) {
  return value.filter((item): item is { credential_code: string } => "credential_code" in item);
}

export function freshLocalSaleQuote(cart: LocalCartDraft) {
  if (!cart.quote) return null;
  return cart.quote.cartFingerprint === localCartSaleFingerprint(cart) ? cart.quote : null;
}

function rewardFinalPayable(
  base: FinalPayableRoundingResult,
  redemption: LocalLoyaltyRedemption | null,
  cart: LocalCartDraft,
  currencyCode: string,
) {
  if (!redemption) return base;
  if (redemption.reservationState !== "RESERVED") throw new Error("LOYALTY_REWARD_RESERVATION_NOT_READY");
  if (redemption.validUntil && Date.parse(redemption.validUntil) <= Date.now()) throw new Error("LOYALTY_REWARD_RESERVATION_EXPIRED");
  if (!cart.selectedMember || cart.selectedMember.customerId !== redemption.customerId) {
    throw new Error("LOYALTY_REWARD_CART_CUSTOMER_MISMATCH");
  }
  const pricing = redemption.pricing;
  if (!pricing || pricing.cartFingerprint !== localCartSaleFingerprint(cart)) {
    throw new Error("LOYALTY_REWARD_QUOTE_REQUIRED");
  }
  if (pricing.currencyCode !== currencyCode) throw new Error("LOYALTY_REWARD_CURRENCY_MISMATCH");
  return {
    ...base,
    preRoundingTotal: Number((pricing.totalAmount - pricing.roundingAmount).toFixed(2)),
    roundingAmount: pricing.roundingAmount,
    totalAmount: pricing.totalAmount,
  } satisfies FinalPayableRoundingResult;
}

function outboxPayload(
  snapshot: LocalCashSaleSnapshot,
  executionMode: "ONLINE" | "OFFLINE",
  promotionClaimEvidence: PromotionClaimEvidence[],
) {
  const lines: CashSaleGatewayLine[] = snapshot.lines.map((line) => ({
    line_id: line.lineId,
    menu_item_id: line.menuItemId,
    menu_variant_id: line.menuVariantId,
    quantity: line.quantity,
    local_unit_price: line.unitPrice,
    price_version_id: line.priceVersionId,
    modifier_option_ids: line.modifiers.map((modifier) => modifier.modifierOptionId),
  }));

  return {
    localTransactionId: snapshot.localTransactionId,
    lines,
    catalogRevision: snapshot.catalogRevision,
    localTotal: snapshot.localTotal,
    tenderedAmount: snapshot.tenderedAmount,
    localChangeAmount: snapshot.changeAmount,
    localCommittedAt: snapshot.localCommittedAt,
    executionMode,
    promotionClaimEvidence,
    saleContext: {
      customer_id: snapshot.selectedMember?.customerId ?? null,
      membership_id: snapshot.selectedMember?.membershipId ?? null,
      claimed_promotion_version_ids: snapshot.claimedPromotionVersionIds,
      loyalty_reward_redemption_id: snapshot.loyaltyRedemption?.redemptionId ?? null,
      quote_fingerprint: snapshot.quote?.quoteFingerprint ?? null,
      quote_evaluated_at: snapshot.quote?.evaluatedAt ?? null,
      promotion_runtime_revision: snapshot.quote?.promotionRuntimeRevision ?? null,
      promotion_quote_source: snapshot.quote?.quoteSource ?? null,
      applied_promotion_version_ids: snapshot.quote?.appliedPromotions.map((promotion) => promotion.promotionVersionId) ?? [],
      final_payable: snapshot.finalPayable ? finalPayableClientSnapshot(snapshot.finalPayable) : null,
    },
  };
}

export async function commitLocalCashSale(input: CommitLocalCashSaleInput) {
  if (!input.cart.lines.length) throw new Error("CASH_SALE_CART_EMPTY");
  if (!input.actor.permission_codes.includes("commerce.sale.commit")) {
    throw new Error("COMMERCE_SALE_COMMIT_PERMISSION_REQUIRED");
  }
  if (input.shift.shift_status !== "OPEN") throw new Error("ACTIVE_POS_SHIFT_REQUIRED");
  if (input.cart.lines.some((line) => line.modifiers.some((modifier) => modifier.quantity !== 1))) {
    throw new Error("MODIFIER_QUANTITY_SYNC_NOT_SUPPORTED");
  }

  const currencyCode = input.cart.lines[0]?.currencyCode ?? "THB";
  if (input.cart.lines.some((line) => line.currencyCode !== currencyCode)) {
    throw new Error("CASH_SALE_CURRENCY_MISMATCH");
  }

  const quote = freshLocalSaleQuote(input.cart);
  if (!quote) throw new Error("POS_SALE_QUOTE_REQUIRED");
  const promotionClaimEvidence = normalizePromotionClaimEvidence(input.promotionClaimEvidence);
  const bearerClaims = bearerClaimEvidence(promotionClaimEvidence);
  const customerClaims = customerClaimEvidence(promotionClaimEvidence);
  const hasPromotionContext = Boolean(
    input.cart.claimedPromotionVersionIds.length
      || quote.appliedPromotions.length
      || quote.promotionDiscountTotal > 0
      || promotionClaimEvidence.length,
  );

  if (bearerClaims.length) {
    if (!input.onlineAtCommit) throw new Error("PROMOTION_CLAIM_REQUIRES_ONLINE");
    if (quote.quoteSource !== "SERVER_COMMERCE_QUOTE_V1") throw new Error("PROMOTION_CLAIM_SERVER_QUOTE_REQUIRED");
    if (!input.promotionClaimEvaluation || input.promotionClaimEvaluation.validation_state === "INVALID") {
      throw new Error("PROMOTION_CLAIM_QUOTE_VALIDATION_REQUIRED");
    }
  }
  if (customerClaims.length) {
    if (!input.cart.selectedMember) throw new Error("PROMOTION_CLAIM_CUSTOMER_REQUIRED");
    if (customerClaims.some((item) => item.customer_id !== input.cart.selectedMember?.customerId)) {
      throw new Error("PROMOTION_CLAIM_CUSTOMER_MISMATCH");
    }
    const customerClaimApplied = quote.appliedPromotions.some(
      (promotion) => promotion.activationCode === "CLAIM_CODE"
        && promotion.offlineCapability === "OFFLINE_RISK_LIMITED",
    );
    if (!customerClaimApplied) throw new Error("PROMOTION_CLAIM_QUOTE_VALIDATION_REQUIRED");
  }

  const hasUnsupportedOfflinePromotion = quote.appliedPromotions.some((promotion) => {
    if (!promotion.offlineCapability || promotion.offlineCapability === "OFFLINE_SAFE") return false;
    return !(
      customerClaims.length
      && promotion.activationCode === "CLAIM_CODE"
      && promotion.offlineCapability === "OFFLINE_RISK_LIMITED"
    );
  });
  if (!input.onlineAtCommit && hasPromotionContext && (quote.requiresOnlineResolution || hasUnsupportedOfflinePromotion)) {
    throw new Error("PROMOTION_CONTEXT_REQUIRES_ONLINE");
  }

  const redemption = await readLocalLoyaltyRedemption(input.identity, input.cart.requestKey);
  const prePromotionTotal = rawCartTotal(input.cart);
  const preRoundingTotal = quote.postPromotionTotal ?? prePromotionTotal;
  const baseFinalPayable = calculateFinalPayableRounding(preRoundingTotal, input.commerceCalculation);
  const finalPayable = rewardFinalPayable(baseFinalPayable, redemption, input.cart, currencyCode);
  const localTotal = finalPayable.totalAmount;
  if (!Number.isFinite(localTotal) || localTotal < 0) throw new Error("CASH_SALE_TOTAL_INVALID");
  if (!Number.isFinite(input.tenderedAmount) || input.tenderedAmount < 0) {
    throw new Error("TENDERED_AMOUNT_INVALID");
  }
  const noPaymentRequired = localTotal === 0;
  if (!noPaymentRequired && input.tenderedAmount < localTotal) {
    throw new Error("TENDERED_AMOUNT_BELOW_LOCAL_TOTAL");
  }

  const tenderedAmount = noPaymentRequired ? 0 : input.tenderedAmount;
  const changeAmount = noPaymentRequired ? 0 : Number((tenderedAmount - localTotal).toFixed(2));
  const localTransactionId = randomId();
  const rootRequestKey = `pos-cash-sale:${randomId()}`;
  const localCommittedAt = new Date().toISOString();
  const scopeKey = saleScope(input.identity);

  const saleSnapshot: LocalCashSaleSnapshot = {
    schema: "LOCAL_CASH_SALE_V1",
    localTransactionId,
    rootRequestKey,
    organizationId: input.identity.organization_id,
    branchId: input.identity.branch_id,
    nodeId: input.identity.node_id,
    commerceChannelId: input.identity.commerce_channel_id,
    performingEmployeeId: input.actor.employee_id,
    responsibleShiftEmployeeId: input.shift.responsible_employee_id,
    shiftId: input.shift.shift_id,
    catalogRevision: input.catalogRevision,
    lines: input.cart.lines,
    selectedMember: input.cart.selectedMember,
    claimedPromotionVersionIds: [...input.cart.claimedPromotionVersionIds],
    quote,
    loyaltyRedemption: redemption,
    finalPayable,
    localTotal,
    tenderedAmount,
    changeAmount,
    currencyCode,
    localCommittedAt,
    canonical: null,
  };

  const paymentSnapshot: LocalCashPaymentSnapshot | null = noPaymentRequired ? null : {
    schema: "LOCAL_CASH_PAYMENT_V1",
    localTransactionId,
    rootRequestKey,
    paymentMethodCode: "CASH",
    amount: localTotal,
    preRoundingTotal: finalPayable.preRoundingTotal,
    roundingAmount: finalPayable.roundingAmount,
    calculationSettingsVersionId: finalPayable.calculationSettingsVersionId,
    tenderedAmount,
    changeAmount,
    currencyCode,
    performingEmployeeId: input.actor.employee_id,
    shiftId: input.shift.shift_id,
    localCommittedAt,
  };

  const outbox: OutboxRecord = {
    requestKey: rootRequestKey,
    scopeKey,
    operation: "SYNC_COMMERCE_CASH_SALE",
    payload: outboxPayload(saleSnapshot, input.onlineAtCommit ? "ONLINE" : "OFFLINE", promotionClaimEvidence),
    state: "PENDING",
    attempts: 0,
    nextAttemptAt: null,
    lastError: null,
    createdAt: localCommittedAt,
    updatedAt: localCommittedAt,
  };

  await posRuntimeDb.transaction("rw", posRuntimeDb.journal, posRuntimeDb.outbox, async () => {
    await posRuntimeDb.journal.put({
      localId: saleLocalId(localTransactionId),
      scopeKey,
      kind: "SALE_ORDER",
      state: "COMMITTED",
      requestKey: rootRequestKey,
      snapshot: saleSnapshot,
      createdAt: localCommittedAt,
      updatedAt: localCommittedAt,
    });
    if (paymentSnapshot) {
      await posRuntimeDb.journal.put({
        localId: paymentLocalId(localTransactionId),
        scopeKey,
        kind: "CASH_PAYMENT",
        state: "COMMITTED",
        requestKey: `${rootRequestKey}:PAYMENT:LOCAL`,
        snapshot: paymentSnapshot,
        createdAt: localCommittedAt,
        updatedAt: localCommittedAt,
      });
    }
    if (redemption) {
      await writeLocalCustomerBenefitUsage(
        input.identity,
        { source_type: redemption.sourceType, source_instance_id: redemption.sourceInstanceId, customer_id: redemption.customerId },
        rootRequestKey,
        localTransactionId,
        localCommittedAt,
      );
    }
    for (const claim of customerClaims) {
      await writeLocalCustomerBenefitUsage(
        input.identity,
        { source_type: "PROMOTION_CLAIM", source_instance_id: claim.claim_instance_id, customer_id: claim.customer_id },
        rootRequestKey,
        localTransactionId,
        localCommittedAt,
      );
    }
    await posRuntimeDb.outbox.put(outbox);
    await posRuntimeDb.journal.delete(input.cart.localCartId);
  });

  return saleSnapshot;
}

type CashSaleOutboxPayload = ReturnType<typeof outboxPayload>;

async function markOutboxFailure(record: OutboxRecord, message: string) {
  const now = new Date().toISOString();
  await posRuntimeDb.outbox.put({
    ...record,
    state: "FAILED",
    attempts: record.attempts + 1,
    nextAttemptAt: new Date(Date.now() + 5_000).toISOString(),
    lastError: message,
    updatedAt: now,
  });
}

export async function syncCashSaleOutbox(
  identity: TrustedTerminalIdentity,
  deviceSecret: string,
  sessionToken: string,
  requestKey: string,
) {
  const record = await posRuntimeDb.outbox.get(requestKey);
  if (!record || record.operation !== "SYNC_COMMERCE_CASH_SALE") throw new Error("CASH_SALE_OUTBOX_NOT_FOUND");
  if (record.state === "ACKED") {
    const sale = await posRuntimeDb.journal.where("requestKey").equals(requestKey).first();
    return sale?.snapshot as LocalCashSaleSnapshot | undefined;
  }

  const baseUrl = getGatewayBaseUrl();
  if (!baseUrl) {
    await markOutboxFailure(record, "POS_GATEWAY_NOT_CONFIGURED");
    throw new Error("POS_GATEWAY_NOT_CONFIGURED");
  }

  const payload = record.payload as CashSaleOutboxPayload;
  const now = new Date().toISOString();
  await posRuntimeDb.outbox.put({ ...record, state: "SENDING", updatedAt: now });

  const client = new PosCashSaleGatewayClient({ baseUrl });
  const result = await client.sync({
    deviceSecret,
    sessionToken,
    localTransactionId: payload.localTransactionId,
    lines: payload.lines,
    catalogRevision: payload.catalogRevision,
    localTotal: payload.localTotal,
    tenderedAmount: payload.tenderedAmount,
    localChangeAmount: payload.localChangeAmount,
    localCommittedAt: payload.localCommittedAt,
    executionMode: payload.executionMode,
    requestKey,
    promotionClaimEvidence: payload.promotionClaimEvidence,
    clientContext: {
      runtime: "WEBPOS",
      terminal_node_id: identity.node_id,
      local_transaction_id: payload.localTransactionId,
      local_commit_authority: true,
      sale_context: payload.saleContext ?? {},
    },
  });

  if (!result.ok || !result.data) {
    await markOutboxFailure(record, result.error ?? "COMMERCE_CASH_SALE_SYNC_FAILED");
    throw new Error(result.error ?? "COMMERCE_CASH_SALE_SYNC_FAILED");
  }

  const saleRecord = await posRuntimeDb.journal.where("requestKey").equals(requestKey).first();
  if (!saleRecord || saleRecord.kind !== "SALE_ORDER") {
    await markOutboxFailure(record, "LOCAL_SALE_JOURNAL_NOT_FOUND");
    throw new Error("LOCAL_SALE_JOURNAL_NOT_FOUND");
  }

  const saleSnapshot = saleRecord.snapshot as LocalCashSaleSnapshot;
  const ackedAt = new Date().toISOString();
  const sanitizedPayload: CashSaleOutboxPayload = { ...payload, promotionClaimEvidence: [] };
  await posRuntimeDb.transaction("rw", posRuntimeDb.journal, posRuntimeDb.outbox, async () => {
    await posRuntimeDb.journal.put({
      ...saleRecord,
      state: "CONFIRMED",
      snapshot: { ...saleSnapshot, canonical: result.data },
      updatedAt: ackedAt,
    });
    await posRuntimeDb.outbox.put({
      ...record,
      payload: sanitizedPayload,
      state: "ACKED",
      attempts: record.attempts + 1,
      nextAttemptAt: null,
      lastError: null,
      updatedAt: ackedAt,
    });
  });
  if (saleSnapshot.loyaltyRedemption) {
    await acknowledgeLocalCustomerBenefitUsage(
      identity,
      saleSnapshot.loyaltyRedemption.sourceType,
      saleSnapshot.loyaltyRedemption.sourceInstanceId,
    );
    await clearLocalLoyaltyRedemption(identity);
  }
  for (const claim of customerClaimEvidence(payload.promotionClaimEvidence)) {
    await acknowledgeLocalCustomerBenefitUsage(identity, "PROMOTION_CLAIM", claim.claim_instance_id);
  }

  return { ...saleSnapshot, canonical: result.data };
}

export async function syncPendingCashSales(identity: TrustedTerminalIdentity, deviceSecret: string, sessionToken: string) {
  const pending = await posRuntimeDb.outbox
    .where("operation")
    .equals("SYNC_COMMERCE_CASH_SALE")
    .filter((record) => record.state !== "ACKED")
    .sortBy("createdAt");

  const results: Array<{ requestKey: string; ok: boolean; error?: string }> = [];
  for (const record of pending) {
    if (record.scopeKey !== saleScope(identity)) continue;
    if (record.nextAttemptAt && Date.parse(record.nextAttemptAt) > Date.now()) continue;
    try {
      await syncCashSaleOutbox(identity, deviceSecret, sessionToken, record.requestKey);
      results.push({ requestKey: record.requestKey, ok: true });
    } catch (error) {
      results.push({ requestKey: record.requestKey, ok: false, error: error instanceof Error ? error.message : "COMMERCE_CASH_SALE_SYNC_FAILED" });
    }
  }
  return results;
}
